<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Purchase orders</h1>
            <p class="text-muted mb-0">
              Manage and track purchase orders issued to vendors
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              @click="refreshAll"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create purchase order
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Purchase Order Insights -->
    <CRow class="mb-4">
      <!-- Purchase Order Summary -->
      <CCol lg="8" class="mb-4 mb-lg-0">
        <CCard class="h-100 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Purchase order summary</h2>
            <div class="small text-muted">All purchase orders · trend for the last 6 months</div>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Total orders</span>
                    <span class="inv-kpi-chip chip-navy"><CIcon name="cil-cart" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ orderSummary.totalCount }}</div>
                  <div class="inv-kpi-sub" :class="orderSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                    <CIcon :name="orderSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ Math.abs(orderSummary.countChange) }}% vs last month
                  </div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Accepted</span>
                    <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(orderSummary.acceptedAmount) }}</div>
                  <div class="inv-kpi-sub">{{ orderSummary.acceptedCount }} order{{ orderSummary.acceptedCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Awaiting</span>
                    <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(orderSummary.pendingAmount) }}</div>
                  <div class="inv-kpi-sub">{{ orderSummary.pendingCount }} order{{ orderSummary.pendingCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Cancelled or rejected</span>
                    <span class="inv-kpi-chip chip-danger"><CIcon name="cil-x-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ formatCurrency(orderSummary.cancelledAmount) }}</div>
                  <div class="inv-kpi-sub">{{ orderSummary.cancelledCount }} order{{ orderSummary.cancelledCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
            </CRow>
            <PurchaseOrderTrendChart
              :chartData="orderTrendData"
              style="height: 250px"
            />
          </CCardBody>
        </CCard>
      </CCol>

      <!-- Status Distribution -->
      <CCol lg="4">
        <CCard class="h-100 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Status distribution</h2>
            <div class="small text-muted">All purchase orders by status</div>
          </CCardHeader>
          <CCardBody
            class="pt-0 pb-4 px-4 d-flex flex-column align-items-center justify-content-center"
          >
            <div v-if="statusDistribution.length" style="height: 200px; width: 200px" class="mb-4">
              <PurchaseOrderStatusChart :chartData="statusDistributionData" />
            </div>
            <div class="w-100">
              <div
                v-for="status in statusDistribution"
                :key="status.name"
                class="d-flex align-items-center mb-2"
              >
                <CBadge :color="badgeColor('purchaseOrder', status.name)" class="mr-2">
                  {{ badgeLabel("purchaseOrder", status.name) }}
                </CBadge>
                <div class="flex-grow-1"></div>
                <div class="text-right">
                  <strong>{{ status.count }}</strong>
                  <span class="text-muted ml-1">({{ status.percentage }}%)</span>
                </div>
              </div>
              <div v-if="statusDistribution.length === 0" class="text-center text-muted small py-3">
                No purchase orders yet
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Purchase Order List -->
    <CCard class="mb-4 border-0">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">
          Purchase order list
          <CBadge color="light" class="ml-2">
            {{ hasActiveFilters ? `${computedItems.length} of ${items.length}` : items.length }}
          </CBadge>
        </h2>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Filters -->
        <CRow class="mb-3">
          <CCol>
            <CButton color="light" @click="showFilters = !showFilters">
              <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
              {{ showFilters ? "Hide filters" : "Show filters" }}
            </CButton>
            <CButton
              v-if="hasActiveFilters"
              color="link"
              class="ml-2"
              @click="resetFilters"
            >
              Clear filters
            </CButton>
          </CCol>
        </CRow>
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="PO number"
                    v-model="filters.orderNumber"
                    placeholder="Search PO number"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Vendor"
                    v-model="filters.vendorName"
                    placeholder="Search vendor"
                  />
                </CCol>
                <CCol md="2">
                  <CSelect
                    label="Status"
                    :value.sync="filters.status"
                    :options="statusFilterOptions"
                  />
                </CCol>
                <CCol md="2">
                  <CInput label="From" type="date" v-model="filters.dateFrom" />
                </CCol>
                <CCol md="2">
                  <CInput
                    label="To"
                    type="date"
                    v-model="filters.dateTo"
                    :is-valid="dateRangeValid ? null : false"
                    invalid-feedback="Choose a date on or after the From date"
                  />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Filtered Results Summary -->
        <CCard v-if="!loading && !loadError && computedItems.length > 0" class="mb-3 inv-summary-card">
          <CCardBody class="p-3">
            <div class="d-flex align-items-center mb-3">
              <CIcon name="cil-calculator" class="mr-2 text-brand-navy" />
              <span class="inv-summary-title">Summary of the orders shown</span>
              <CBadge class="ml-2 badge-soft">{{ filteredSummary.totalCount }} order{{ filteredSummary.totalCount !== 1 ? 's' : '' }}</CBadge>
            </div>
            <CRow>
              <CCol lg="3" md="6" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Grand total</div><div class="inv-stat-val text-brand-navy">{{ formatCurrency(filteredSummary.totalAmount) }}</div></div>
              </CCol>
              <CCol lg="3" md="6" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Accepted</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.acceptedAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.acceptedCount }} order{{ filteredSummary.acceptedCount === 1 ? '' : 's' }}</div></div>
              </CCol>
              <CCol lg="3" md="6" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Cancelled or rejected</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.cancelledAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.cancelledCount }} order{{ filteredSummary.cancelledCount === 1 ? '' : 's' }}</div></div>
              </CCol>
              <CCol lg="3" md="6" sm="6" class="mb-2">
                <div class="inv-stat"><div class="inv-stat-label">Average</div><div class="inv-stat-val">{{ formatCurrency(filteredSummary.averageAmount) }}</div><div class="inv-stat-meta">per order</div></div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Loading -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading purchase orders</span>
          </div>
          <p class="mt-3 text-muted">Loading purchase orders…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h3 class="h6 font-weight-bold">Couldn't load purchase orders</h3>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshAll">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-cart" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No purchase orders yet</h3>
          <p class="text-muted mb-3">Create a purchase order to send to a vendor.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Create purchase order
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!computedItems.length" class="text-center py-5">
          <CIcon name="cil-filter" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No purchase orders match your filters</h3>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <div v-if="loading" class="progress mb-2" style="height: 3px">
            <div class="progress-bar progress-bar-striped progress-bar-animated w-100" role="progressbar" aria-label="Refreshing"></div>
          </div>

          <!-- Desktop table -->
          <div class="d-none d-lg-block table-responsive">
            <table class="table table-hover border">
              <thead>
                <tr>
                  <th style="width: 50px">#</th>
                  <th>PO number</th>
                  <th>Vendor</th>
                  <th>Order date</th>
                  <th class="text-right">Amount</th>
                  <th>Status</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in computedItems" :key="item.id">
                  <td class="text-muted">{{ index + 1 }}</td>
                  <td>
                    <router-link
                      :to="`/tenants/PurchaseOrder/${item.id}`"
                      class="font-weight-bold text-monospace"
                    >{{ item.orderNo }}</router-link>
                  </td>
                  <td>{{ item.vendorName }}</td>
                  <td>{{ item.displayOrderDate }}</td>
                  <td class="text-right font-weight-bold">
                    {{ formatCurrency(item.computedAmount) }}
                  </td>
                  <td>
                    <CBadge
                      :color="badgeColor('purchaseOrder', item.statusDescription || 'Draft')"
                      class="px-2 py-1"
                    >
                      {{ badgeLabel("purchaseOrder", item.statusDescription || "Draft") }}
                    </CBadge>
                  </td>
                  <td class="text-right">
                    <CButtonGroup>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Edit ${item.orderNo}`"
                        title="Edit"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`View ${item.orderNo}`"
                        title="View"
                        @click="onView(item)"
                      >
                        <CIcon name="cil-eye" />
                      </CButton>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Delete ${item.orderNo}`"
                        title="Delete"
                        @click="showDeleteConfirmation(item)"
                      >
                        <CIcon name="cil-trash" />
                      </CButton>
                    </CButtonGroup>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <CCard v-for="item in computedItems" :key="item.id" class="mb-3 border">
              <CCardBody class="p-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <router-link
                    :to="`/tenants/PurchaseOrder/${item.id}`"
                    class="font-weight-bold text-monospace"
                  >{{ item.orderNo }}</router-link>
                  <CBadge :color="badgeColor('purchaseOrder', item.statusDescription || 'Draft')">
                    {{ badgeLabel("purchaseOrder", item.statusDescription || "Draft") }}
                  </CBadge>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-muted small">Vendor</span>
                  <span>{{ item.vendorName }}</span>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-muted small">Order date</span>
                  <span>{{ item.displayOrderDate }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted small">Amount</span>
                  <span class="font-weight-bold">{{ formatCurrency(item.computedAmount) }}</span>
                </div>
                <div class="d-flex justify-content-between">
                  <CButton color="light" @click="onEdit(item)">
                    <CIcon name="cil-pencil" /> Edit
                  </CButton>
                  <CButton
                    color="light"
                    class="text-danger"
                    :aria-label="`Delete ${item.orderNo}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" /> Delete
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation Modal -->
    <CModal
      :title="`Delete purchase order ${itemToDelete.orderNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        Purchase order <strong>{{ itemToDelete.orderNo }}</strong>
        <span v-if="itemToDelete.vendorName && itemToDelete.vendorName !== '—'"> for {{ itemToDelete.vendorName }}</span>
        ({{ formatCurrency(itemToDelete.computedAmount) }}) will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete purchase order" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PurchaseOrderApi from "@/lib/purchaseOrderApi";
import PurchaseOrderTrendChart from "../widgets/PurchaseOrderTrendChart";
import PurchaseOrderStatusChart from "../widgets/PurchaseOrderStatusChart";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";
import { badgeColor, badgeLabel, badgeChartColor } from "@/utils/statusBadges";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Last `n` calendar months (oldest first) as "yyyy-MM" keys, in Malaysia time. */
function lastMonthKeys(n) {
  const [y, m] = mytToday().split("-").map(Number);
  const keys = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

export default {
  name: "PurchaseOrderList",
  components: {
    PurchaseOrderTrendChart,
    PurchaseOrderStatusChart,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      api: new PurchaseOrderApi(),
      warningModal: false,
      itemToDelete: {},
      showFilters: false,
      filters: {
        orderNumber: "",
        vendorName: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      },
      statusFilterOptions: [
        { value: "", label: "All statuses" },
        { value: "Draft", label: "Draft" },
        { value: "Sent", label: "Sent" },
        { value: "Accepted", label: "Accepted" },
        { value: "Rejected", label: "Rejected" },
        { value: "Cancelled", label: "Cancelled" },
      ],
      // Real analytics source — all purchase orders for the org-wide summary.
      allPurchaseOrders: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Purchase orders"]);
    this.refreshTable();
    this.fetchSummary(); // Load org-wide POs for real analytics
  },
  computed: {
    computedItems() {
      return this.items
        .filter((item) => this.matchesFilters(item))
        .map((item) => ({
          ...item,
          orderNo: this.getOrderNumber(item),
          vendorName: this.getVendorName(item),
          displayOrderDate: formatDate(this.getOrderDate(item)) || "—",
          computedAmount: this.poAmount(item),
        }));
    },

    hasActiveFilters() {
      return Object.values(this.filters).some((f) => f !== "");
    },

    dateRangeValid() {
      const f = this.filters;
      return !f.dateFrom || !f.dateTo || f.dateTo >= f.dateFrom;
    },

    // Summary for the currently filtered/visible list
    filteredSummary() {
      const list = this.computedItems;
      let totalAmount = 0, acceptedAmount = 0, acceptedCount = 0, cancelledAmount = 0, cancelledCount = 0;
      list.forEach((po) => {
        const amt = po.computedAmount || 0;
        totalAmount += amt;
        if (po.statusDescription === "Accepted") { acceptedAmount += amt; acceptedCount++; }
        if (po.statusDescription === "Cancelled" || po.statusDescription === "Rejected") { cancelledAmount += amt; cancelledCount++; }
      });
      const averageAmount = list.length > 0 ? totalAmount / list.length : 0;
      return { totalAmount, acceptedAmount, acceptedCount, cancelledAmount, cancelledCount, averageAmount, totalCount: list.length };
    },

    // ===== Real analytics (computed from allPurchaseOrders) =====
    orderSummary() {
      const all = this.allPurchaseOrders || [];
      let acceptedAmount = 0, acceptedCount = 0, pendingAmount = 0, pendingCount = 0, cancelledAmount = 0, cancelledCount = 0;
      all.forEach((po) => {
        const amt = this.poAmount(po);
        const s = po.statusDescription;
        if (s === "Accepted") { acceptedAmount += amt; acceptedCount++; }
        else if (s === "Cancelled" || s === "Rejected") { cancelledAmount += amt; cancelledCount++; }
        else { pendingAmount += amt; pendingCount++; } // Draft + Sent = awaiting
      });
      const [lastKey, thisKey] = lastMonthKeys(2);
      const datedIn = (key) => all.filter((po) => this.monthKey(this.getOrderDate(po)) === key).length;
      const cThis = datedIn(thisKey), cLast = datedIn(lastKey);
      const countChange = cLast ? Math.round(((cThis - cLast) / cLast) * 1000) / 10 : (cThis > 0 ? 100 : 0);
      return { totalCount: all.length, countChange, acceptedAmount, acceptedCount, pendingAmount, pendingCount, cancelledAmount, cancelledCount };
    },

    statusDistribution() {
      const all = this.allPurchaseOrders || [];
      const counts = {};
      all.forEach((po) => {
        const s = po.statusDescription || "Draft";
        counts[s] = (counts[s] || 0) + 1;
      });
      const total = all.length || 1;
      // Stable order for known statuses
      const order = ["Accepted", "Sent", "Draft", "Rejected", "Cancelled"];
      return Object.keys(counts)
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
        .map((name) => ({
          name,
          count: counts[name],
          percentage: Math.round((counts[name] / total) * 1000) / 10,
        }));
    },

    statusDistributionData() {
      return {
        labels: this.statusDistribution.map((s) => badgeLabel("purchaseOrder", s.name)),
        datasets: [
          {
            data: this.statusDistribution.map((s) => s.count),
            backgroundColor: this.statusDistribution.map((s) => badgeChartColor("purchaseOrder", s.name)),
            borderWidth: 0,
          },
        ],
      };
    },

    // Trend chart — last 6 months total PO value by month (real)
    orderTrendData() {
      const months = lastMonthKeys(6);
      const all = this.allPurchaseOrders || [];
      const inMonth = (po, key) => this.monthKey(this.getOrderDate(po)) === key;
      const ordered = months.map((key) =>
        all.filter((po) => inMonth(po, key)).reduce((a, po) => a + this.poAmount(po), 0)
      );
      const accepted = months.map((key) =>
        all
          .filter((po) => po.statusDescription === "Accepted" && inMonth(po, key))
          .reduce((a, po) => a + this.poAmount(po), 0)
      );
      const primary = badgeChartColor("invoice", "Approve"); // theme primary
      const success = badgeChartColor("purchaseOrder", "Accepted");
      return {
        labels: months.map((key) => MONTH_NAMES[Number(key.slice(5, 7)) - 1]),
        datasets: [
          { label: "Ordered", backgroundColor: "transparent", borderColor: primary, pointBackgroundColor: primary, borderWidth: 2, fill: false, tension: 0.4, data: ordered },
          { label: "Accepted", backgroundColor: "transparent", borderColor: success, pointBackgroundColor: success, borderWidth: 2, fill: false, tension: 0.4, data: accepted },
        ],
      };
    },
  },

  methods: {
    badgeColor,
    badgeLabel,
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
    monthKey(value) {
      return asDateString(value).slice(0, 7);
    },

    // PO total derived from line items (model has no totalAmount; mirrors PurchaseOrder.vue grandTotal)
    poAmount(po) {
      if (po == null) return 0;
      const direct = Number(po.totalAmount) || Number(po.grandTotal) || 0;
      if (direct > 0) return direct;
      const items = po.items || po.Items || [];
      return items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
    },
    getOrderNumber(item) {
      return item.orderNumber || item.orderNo || item.OrderNumber || "—";
    },
    getOrderDate(item) {
      return item.issuedDate || item.orderDate || item.date || item.IssuedDate;
    },
    getVendorName(item) {
      if (item.vendor && item.vendor.name) return item.vendor.name;
      return item.vendorName || "—";
    },

    matchesFilters(item) {
      const f = this.filters;
      if (f.orderNumber) {
        const no = (this.getOrderNumber(item) || "").toString().toLowerCase();
        if (!no.includes(f.orderNumber.toLowerCase())) return false;
      }
      if (f.vendorName) {
        const v = (this.getVendorName(item) || "").toLowerCase();
        if (!v.includes(f.vendorName.toLowerCase())) return false;
      }
      if (f.status) {
        if ((item.statusDescription || "") !== f.status) return false;
      }
      const d = asDateString(this.getOrderDate(item));
      if (f.dateFrom && d && d < f.dateFrom) return false;
      if (f.dateTo && this.dateRangeValid && d && d > f.dateTo) return false;
      return true;
    },

    resetFilters() {
      this.filters = {
        orderNumber: "",
        vendorName: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      };
    },
    refreshAll() {
      this.refreshTable();
      this.fetchSummary();
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          const list = (response && (response.result?.data ?? response.data ?? response.result)) || [];
          this.items = Array.isArray(list) ? list : [];
        })
        .catch((err) => {
          console.error("Failed to load purchase orders", err);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Org-wide analytics source (degrades gracefully; table still works on failure)
    fetchSummary() {
      this.api
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          this.allPurchaseOrders = (r && (r.result?.data ?? r.data ?? r.result)) || [];
          if (!Array.isArray(this.allPurchaseOrders)) this.allPurchaseOrders = [];
        })
        .catch(() => { /* analytics degrade gracefully */ });
    },
    onEdit(item) {
      this.$router.push({
        path: `/tenants/PurchaseOrder/${item.id}`,
      });
    },
    onView(item) {
      this.$router.push({
        path: `/tenants/PurchaseOrder/${item.id}`,
        query: { view: "readonly" },
      });
    },
    confirmDelete() {
      this.deleting = true;
      const no = this.itemToDelete.orderNo;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success(`Purchase order ${no} deleted`);
          this.warningModal = false;
          this.refreshAll();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete purchase order ${no}. ${helper.getErrorMessage(data, "Please try again.")}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    addNew() {
      this.$router.push({ path: "/tenants/PurchaseOrder" });
    },
  },
};
</script>

<style scoped>
.spinner-border {
  width: 2rem;
  height: 2rem;
}

/* ============================================================= */
/* Brand — navy #1E3A5F / emerald #10B981 (same as the other list screens) */
/* ============================================================= */
h1 { color: #1e3a5f; }
::v-deep .btn-primary {
  background-color: #10b981;
  border-color: #10b981;
}
::v-deep .btn-primary:hover,
::v-deep .btn-primary:focus,
::v-deep .btn-primary:not(:disabled):not(.disabled):active {
  background-color: #059669;
  border-color: #059669;
}
::v-deep .btn-outline-primary {
  color: #1e3a5f;
  border-color: #1e3a5f;
}
::v-deep .btn-outline-primary:hover,
::v-deep .btn-outline-primary.active,
::v-deep .btn-outline-primary:not(:disabled):not(.disabled).active {
  background-color: #1e3a5f;
  border-color: #1e3a5f;
  color: #fff;
}

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; }
.inv-kpi-sub { font-size: 0.74rem; color: #64748b; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

/* Filtered Results Summary */
.inv-summary-card { border: 1px solid #e2e8f0; border-radius: 14px; }
.inv-summary-title { font-size: 0.85rem; font-weight: 700; color: #1e3a5f; }
.badge-soft { background: #eef2f7; color: #1e3a5f; font-weight: 600; }
.inv-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 0.6rem 0.7rem; height: 100%; }
.inv-stat-label { font-size: 0.75rem; color: #64748b; }
.inv-stat-val { font-size: 0.95rem; font-weight: 700; color: #334155; }
.inv-stat-meta { font-size: 0.7rem; color: #64748b; }
.text-brand-navy { color: #1e3a5f; }

::v-deep .table thead th {
  font-size: 0.8rem;
  color: #64748b;
  border-top: none;
  border-bottom: 1px solid #e2e8f0;
}
::v-deep .table tbody td { vertical-align: middle; }
</style>
